import React, { useEffect, useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import { Link } from 'react-router-dom';
import { Upload as UploadIcon, PlayCircle, Users, DollarSign, TrendingUp, Clock, Eye, Trash2, Radio, Loader2, Megaphone, Flame, Youtube, Music, Film, GraduationCap, Sparkles, Check, ExternalLink } from 'lucide-react';
import { formatCount } from '../mock';
import { toast } from 'sonner';
import { listVideos, getChannel, deleteVideo, resolveFileUrl, updateChannel } from '../lib/api';
import { channelSpotify } from '../lib/api';

function Stat({ icon: Ic, label, value, delta, color }) {
  return (
    <div className="card-shell p-5">
      <div className="flex items-center justify-between">
        <div className="w-10 h-10 rounded-lg grid place-items-center" style={{ background: color + '22', border: '1px solid ' + color + '55' }}>
          <Ic className="w-5 h-5" style={{ color }} />
        </div>
        {delta && <span className="text-[12px] text-[#22d3ee]">{delta}</span>}
      </div>
      <div className="mt-3 font-display text-[30px] leading-none">{value}</div>
      <div className="text-[12px] text-white/55 mt-1">{label}</div>
    </div>
  );
}

export default function Studio() {
  const { user } = useApp();
  const [channel, setChannel] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      if (user?.channel_id) {
        const [c, v] = await Promise.all([ getChannel(user.channel_id), listVideos({ channel_id: user.channel_id, limit: 200 }) ]);
        setChannel(c); setVideos(v);
      } else { setChannel(null); setVideos([]); }
    } catch { toast.error('Failed to load studio'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.channel_id]);

  const totalViews = useMemo(() => videos.reduce((s, v) => s + (v.views || 0), 0), [videos]);
  const estRevenue = Math.round(totalViews * 0.004);

  if (loading) return <div className="flex items-center gap-2 text-white/60 py-20"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;

  if (!user?.channel_id || !channel) {
    return (
      <div className="py-10">
        <div className="card-shell p-6 max-w-[560px]">
          <h2 className="font-display text-[24px]">You don’t have a channel yet</h2>
          <p className="text-white/65 mt-1 text-[14px]">Admin accounts don’t have a default channel. Sign up as a creator to see the Studio dashboard.</p>
        </div>
      </div>
    );
  }

  const onDelete = async (id) => {
    try { await deleteVideo(id); toast.success('Deleted'); setVideos(v => v.filter(x => x.id !== id)); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="chip">Creator Studio</div>
          <h1 className="font-display text-[32px] md:text-[40px] mt-3 leading-none">{channel.name} dashboard</h1>
          <p className="text-white/60 text-[14px] mt-1">Plan: <span className="text-white capitalize font-semibold">{user?.plan || 'free'}</span></p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/upload" className="btn-primary"><UploadIcon className="w-4 h-4" /> Upload video</Link>
          <Link to="/ads" className="btn-ghost"><Megaphone className="w-4 h-4" /> Ads & Sponsors</Link>
          <button onClick={() => toast('Opening live studio…')} className="btn-ghost"><Radio className="w-4 h-4" /> Go live</button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Stat icon={Eye}        color="#ff2e63"  label="Views (all time)" value={formatCount(totalViews)} delta="+12.4%" />
        <Stat icon={Users}      color="#22d3ee"  label="Subscribers" value={formatCount(channel.subs_count || 0)} delta="+2.1%" />
        <Stat icon={PlayCircle} color="#a78bfa"  label="Videos" value={videos.length} />
        <Stat icon={DollarSign} color="#34d399"  label="Est. revenue" value={'$' + estRevenue.toLocaleString()} delta="+18.2%" />
      </div>

      <NichePicker channel={channel} onUpdate={ch => setChannel(ch)} />

      <SpotifyLinkCard channel={channel} onUpdate={ch => setChannel(ch)} />

      <MigrationCta />

      {channel?.niche === 'musician' && <MusicBizBanner />}

      <div className="mt-8 grid lg:grid-cols-[2fr_1fr] gap-5">
        <div className="card-shell p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="row-title text-white">Watch time (last 7 days)</h3>
            <span className="chip"><TrendingUp className="w-3.5 h-3.5" /> Up 18%</span>
          </div>
          <div className="h-48 flex items-end gap-2">
            {[32, 44, 36, 58, 50, 72, 82].map((h, i) => (
              <div key={i} className="flex-1">
                <div className="w-full rounded-t" style={{ height: `${h}%`, background: 'linear-gradient(180deg,#ff2e63,#4a0a19)' }} />
                <div className="text-center text-[11px] text-white/45 mt-1">{['Mon','Tue','Wed','Thu','Fri','Sat','Sun'][i]}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="card-shell p-5">
          <h3 className="row-title text-white mb-4">Audience</h3>
          <div className="space-y-3 text-[13px]">
            {[ { c: 'United States', p: 42 }, { c: 'United Kingdom', p: 18 }, { c: 'Canada', p: 12 }, { c: 'Germany', p: 10 }, { c: 'Other', p: 18 } ].map(r => (
              <div key={r.c}>
                <div className="flex items-center justify-between"><span>{r.c}</span><span className="text-white/55">{r.p}%</span></div>
                <div className="mt-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full" style={{ width: r.p + '%', background: 'linear-gradient(90deg,#ff2e63,#22d3ee)' }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card-shell mt-8 overflow-hidden">
        <div className="p-5 flex items-center justify-between">
          <h3 className="row-title text-white">Your content</h3>
          <Link to="/upload" className="btn-ghost"><UploadIcon className="w-4 h-4" /> New upload</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13.5px]">
            <thead className="bg-white/[0.03] text-white/55">
              <tr>
                <th className="text-left px-5 py-2 font-semibold">Video</th>
                <th className="text-left px-3 py-2 font-semibold">Type</th>
                <th className="text-right px-3 py-2 font-semibold">Views</th>
                <th className="text-right px-3 py-2 font-semibold">Likes</th>
                <th className="text-right px-3 py-2 font-semibold">Uploaded</th>
                <th className="px-5 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {videos.map(v => (
                <tr key={v.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-5 py-3">
                    <Link to={`/watch/${v.id}`} className="flex items-center gap-3">
                      <div className="w-20 shrink-0 thumb" style={{ aspectRatio: '16/9' }}>
                        <img src={resolveFileUrl(v.thumb)} alt={v.title} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold line-clamp-1">{v.title}</div>
                        <div className="text-[12px] text-white/50 flex items-center gap-1"><Clock className="w-3 h-3" /> {v.duration}</div>
                      </div>
                    </Link>
                  </td>
                  <td className="px-3 py-3 capitalize">{v.type}</td>
                  <td className="px-3 py-3 text-right">{formatCount(v.views)}</td>
                  <td className="px-3 py-3 text-right">{formatCount(v.likes_count)}</td>
                  <td className="px-3 py-3 text-right text-white/65">{v.uploaded_at}</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => onDelete(v.id)} className="text-white/60 hover:text-[#ff6b8a]" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {videos.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-white/50">No videos yet. <Link to="/upload" className="text-[#ff6b8a] hover:underline">Upload your first video</Link></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const NICHES = [
  { id: 'filmmaker', label: 'Indie filmmaker', icon: Film,          color: '#ff2e63', desc: 'Short-form docs, narrative, art' },
  { id: 'educator',  label: 'Educator',        icon: GraduationCap, color: '#22d3ee', desc: 'Lessons, tutorials, lectures' },
  { id: 'musician',  label: 'Music artist',    icon: Music,         color: '#a78bfa', desc: 'Music videos, live sessions, beats' },
  { id: 'general',   label: 'General creator', icon: Sparkles,      color: '#ffcf52', desc: 'Anything and everything' },
];

function NichePicker({ channel, onUpdate }) {
  const [saving, setSaving] = useState('');
  const current = channel?.niche || 'general';
  const save = async (niche) => {
    if (niche === current) return;
    setSaving(niche);
    try {
      const updated = await updateChannel(channel.id, { niche });
      onUpdate(updated);
      toast.success('Niche updated');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setSaving(''); }
  };
  return (
    <div className="card-shell p-5 mt-8" data-testid="niche-picker">
      <div className="flex items-center gap-2 text-[#ffcf52]"><Flame className="w-4 h-4" /><span className="uppercase text-[11px] tracking-widest font-bold">Your forge</span></div>
      <h3 className="row-title text-white mt-1.5 mb-3">What do you create?</h3>
      <p className="text-white/55 text-[13px] mb-4">Pick a niche — we&apos;ll tailor home rows, recommendations, and creator tools for you.</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {NICHES.map(n => {
          const on = current === n.id;
          return (
            <button key={n.id} onClick={() => save(n.id)} disabled={saving === n.id}
              className={`card-shell p-4 text-left transition ${on ? 'ring-2' : 'hover:ring-1 hover:ring-white/30'}`}
              style={on ? { borderColor: n.color + 'aa', boxShadow: `0 0 0 2px ${n.color}55` } : {}}
              data-testid={`niche-${n.id}`}>
              <div className="flex items-center justify-between">
                <div className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: n.color + '22', border: '1px solid ' + n.color + '55' }}>
                  <n.icon className="w-4 h-4" style={{ color: n.color }} />
                </div>
                {on && <Check className="w-4 h-4 text-[#22d3ee]" />}
              </div>
              <div className="font-display text-[17px] mt-3">{n.label}</div>
              <div className="text-[12px] text-white/55 mt-0.5">{n.desc}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MigrationCta() {
  return (
    <Link to="/migrate/youtube" className="card-shell p-5 mt-6 flex items-center justify-between hover-lift block"
          style={{ borderColor: 'rgba(255,0,0,0.35)', background: 'linear-gradient(90deg, rgba(255,0,0,0.08), rgba(255,122,0,0.05))' }}
          data-testid="studio-migrate-cta">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl grid place-items-center" style={{ background: 'rgba(255,0,0,0.15)', border: '1px solid rgba(255,0,0,0.4)' }}>
          <Youtube className="w-5 h-5 text-[#ff0000]" />
        </div>
        <div>
          <div className="font-semibold text-[15px]">Bring your YouTube videos over</div>
          <div className="text-[13px] text-white/60">Pick up to 25 videos from your YouTube channel and forge them into VidCreForge in one click.</div>
        </div>
      </div>
      <div className="text-[#ff7a00] text-[13px] flex items-center gap-1">Migrate <Flame className="w-4 h-4" /></div>
    </Link>
  );
}

function MusicBizBanner() {
  return (
    <a href="https://musicbizbasic.com" target="_blank" rel="noopener noreferrer"
       className="card-shell p-5 mt-6 flex items-center justify-between hover-lift block"
       style={{ borderColor: 'rgba(167,139,250,0.45)', background: 'linear-gradient(90deg, rgba(167,139,250,0.12), rgba(34,211,238,0.05))' }}
       data-testid="musicbiz-banner">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl grid place-items-center" style={{ background: 'rgba(167,139,250,0.18)', border: '1px solid rgba(167,139,250,0.45)' }}>
          <Music className="w-5 h-5 text-[#a78bfa]" />
        </div>
        <div>
          <div className="font-semibold text-[15px]">Music artist? Level up your business at <span className="text-[#a78bfa]">MusicBizBasic.com</span></div>
          <div className="text-[13px] text-white/60">Royalties, sync licensing, tour logistics — the stuff VidCreForge doesn&apos;t do. Our partner site does.</div>
        </div>
      </div>
      <div className="text-[#a78bfa] text-[13px] flex items-center gap-1">Visit <ExternalLink className="w-4 h-4" /></div>
    </a>
  );
}

function SpotifyLinkCard({ channel, onUpdate }) {
  const [value, setValue] = useState(channel?.spotify_artist_url || '');
  const [saving, setSaving] = useState(false);
  const [live, setLive] = useState(null);
  const [loadingLive, setLoadingLive] = useState(false);

  useEffect(() => { setValue(channel?.spotify_artist_url || ''); }, [channel?.id, channel?.spotify_artist_url]);
  useEffect(() => {
    let cancelled = false;
    if (!channel?.spotify_artist_id) { setLive(null); return; }
    (async () => {
      setLoadingLive(true);
      try { const d = await channelSpotify(channel.id); if (!cancelled) setLive(d); }
      catch { if (!cancelled) setLive(null); }
      setLoadingLive(false);
    })();
    return () => { cancelled = true; };
  }, [channel?.id, channel?.spotify_artist_id]);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateChannel(channel.id, { spotify_artist_url: value.trim() });
      onUpdate(updated);
      toast.success(value.trim() ? 'Spotify linked ✓' : 'Spotify link cleared');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not save'); }
    setSaving(false);
  };

  return (
    <div className="card-shell p-5 mt-6" data-testid="studio-spotify-link">
      <div className="flex items-center gap-2 text-[#1DB954]">
        <Music className="w-4 h-4" />
        <span className="uppercase text-[11px] tracking-widest font-bold">Spotify sync</span>
      </div>
      <h3 className="row-title text-white mt-1.5 mb-1">Link your Spotify artist profile</h3>
      <p className="text-white/55 text-[13px] mb-4">Paste your public Spotify URL — we&apos;ll display your live follower count on your channel header and Artists page.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder="https://open.spotify.com/artist/XXXXXXXXXXXXXXXXXXXXXX"
          className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-[13px] outline-none focus:border-white/30"
          data-testid="studio-spotify-input"
        />
        <button onClick={save} disabled={saving || value === (channel?.spotify_artist_url || '')} className="btn-primary" data-testid="studio-spotify-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
        </button>
      </div>
      {channel?.spotify_artist_id && (
        <div className="mt-3 flex items-center gap-3 text-[12.5px]" data-testid="studio-spotify-linked">
          <span className="chip" style={{ color: '#1DB954', borderColor: '#1DB95455' }}>Linked · {channel.spotify_artist_id}</span>
          {loadingLive && <span className="text-white/50 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> checking…</span>}
          {live && live.artist && (
            <span className="text-white/70">{live.artist.name} · {Intl.NumberFormat('en', { notation: 'compact' }).format(live.artist.followers)} followers</span>
          )}
          {live && live.configured === false && (
            <span className="text-white/50">Live data will appear once Spotify keys are added to deployment secrets.</span>
          )}
        </div>
      )}
    </div>
  );
}


